/**
 * calendar-ics — the calendar-invite channel's template + iCalendar builder.
 *
 * A calendar invite is a message a customer receives, so it belongs beside the
 * others in the composer rather than being buried as an email attachment option.
 * What the author edits is a TEMPLATE (summary, description, location, organiser,
 * reminder); what the send engine emits is RFC 5545 text built from it.
 *
 * The three methods matter and are the whole reason this is not just "attach an
 * ics":
 *   · invite  → METHOD:REQUEST, SEQUENCE:0 — creates the entry
 *   · update  → METHOD:REQUEST, SEQUENCE:n+1, same UID — MOVES the existing entry
 *               instead of adding a second one
 *   · cancel  → METHOD:CANCEL, STATUS:CANCELLED, same UID — removes it
 * A stable UID is therefore load-bearing: it comes from the booking or event, not
 * from the message. Get it wrong and a reschedule leaves two appointments in the
 * customer's calendar, which is the single most common failure of this feature.
 */
export type IcsMethod = "invite" | "update" | "cancel";

export interface IcsTemplate {
  method: IcsMethod;
  /** Calendar entry title. Tokens allowed. */
  summary: string;
  /** Body of the calendar entry. Tokens allowed. */
  description: string;
  /** Free-text location. Tokens allowed. */
  location: string;
  organiserName: string;
  organiserEmail: string;
  /** Minutes before the start for the calendar's own alarm; 0 = none. */
  reminderMins: number;
  /** Token resolving to the stable id of the underlying booking/event. */
  uidToken: string;
  /** Fallback duration when the source has no explicit end. */
  durationMins: number;
}

export const ICS_DEFAULT: IcsTemplate = {
  method: "invite",
  summary: "{{service}} with {{staff}}",
  description: "Your appointment with {{org_name}}.\\n\\nManage or reschedule: {{manage_url}}",
  location: "{{location_address}}",
  organiserName: "{{org_name}}",
  organiserEmail: "no-reply@jrni.example",
  reminderMins: 60,
  uidToken: "{{booking_id}}",
  durationMins: 45,
};

export const ICS_METHODS: { id: IcsMethod; label: string; help: string }[] = [
  { id: "invite", label: "Invitation", help: "Creates the entry in their calendar." },
  { id: "update", label: "Update", help: "Moves the existing entry — same event, new details. Never creates a duplicate." },
  { id: "cancel", label: "Cancellation", help: "Removes the entry they already have." },
];

/** Escaping per RFC 5545 §3.3.11, and the 75-octet line fold. */
const esc = (s: string) => String(s ?? "").replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\r?\n/g, "\\n");
function fold(line: string): string {
  if (line.length <= 73) return line;
  const out: string[] = [];
  let rest = line;
  out.push(rest.slice(0, 73));
  rest = rest.slice(73);
  while (rest.length) { out.push(" " + rest.slice(0, 72)); rest = rest.slice(72); }
  return out.join("\r\n");
}

/** Sample values used when previewing — the real ones come from the booking. */
export interface IcsSample { start: string; end: string; uid: string; stamp: string }
export const ICS_PREVIEW_SAMPLE: IcsSample = {
  start: "20261014T143000Z", end: "20261014T151500Z",
  uid: "bk-8f2c@jrni.example", stamp: "20261007T090000Z",
};

/**
 * Build the iCalendar text. `resolve` maps tokens to values (sample values in
 * preview, real ones at send). `sequence` is the revision — the send engine owns
 * it, and it MUST increase for an update against the same UID.
 */
export function buildIcs(tpl: IcsTemplate, opts?: { resolve?: (s: string) => string; sample?: IcsSample; sequence?: number }): string {
  const r = opts?.resolve || ((s: string) => s);
  const s = opts?.sample || ICS_PREVIEW_SAMPLE;
  const seq = opts?.sequence ?? (tpl.method === "invite" ? 0 : 1);
  const cancel = tpl.method === "cancel";
  const lines: string[] = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//JRNI//Studio Messaging//EN",
    "CALSCALE:GREGORIAN",
    `METHOD:${cancel ? "CANCEL" : "REQUEST"}`,
    "BEGIN:VEVENT",
    `UID:${esc(s.uid)}`,
    `SEQUENCE:${seq}`,
    `DTSTAMP:${s.stamp}`,
    `DTSTART:${s.start}`,
    `DTEND:${s.end}`,
    `SUMMARY:${esc(r(tpl.summary))}`,
    `DESCRIPTION:${esc(r(tpl.description))}`,
    `LOCATION:${esc(r(tpl.location))}`,
    `ORGANIZER;CN=${esc(r(tpl.organiserName))}:mailto:${esc(tpl.organiserEmail)}`,
    "ATTENDEE;CUTYPE=INDIVIDUAL;ROLE=REQ-PARTICIPANT;PARTSTAT=NEEDS-ACTION;RSVP=TRUE:mailto:{{email}}",
    `STATUS:${cancel ? "CANCELLED" : "CONFIRMED"}`,
    "TRANSP:OPAQUE",
  ];
  if (!cancel && tpl.reminderMins > 0) {
    lines.push("BEGIN:VALARM", "ACTION:DISPLAY", `TRIGGER:-PT${tpl.reminderMins}M`, `DESCRIPTION:${esc(r(tpl.summary))}`, "END:VALARM");
  }
  lines.push("END:VEVENT", "END:VCALENDAR");
  return lines.map(fold).join("\r\n");
}

/** Which seeded messages carry which method — used by the harness seed too. */
export const ICS_FOR_MESSAGE: Record<string, IcsMethod> = {
  msg_b_confirmed: "invite",
  msg_b_rescheduled: "update",
  msg_b_cancelled: "cancel",
  msg_confirm_reg: "invite",
  msg_event_changed: "update",
  msg_event_cancelled: "cancel",
};
